import { _axios } from '@/plugins/axios'
import router from '@/router/router'
import QuestionnaireConverter from '@/plugins/converters/questionnaires'
import UserQuestionnaireConverter from '@/plugins/converters/userQuestionnaires'
import i18n from '@/plugins/i18n'

const returnIterationNotFinished = (questionnaire = null) => {
  return questionnaire && questionnaire.iterations
    ? questionnaire.iterations.find(iteration => !iteration.isFinished)
    : state.questionnaire.iterations.find(iteration => !iteration.isFinished)
}

/**
 * Retourne l'identifiant de la page demandée ou la commande "reset"
 *
 * @param pages {UserPageAir[]}
 * @param pageNumber {Number}
 * @return {Number|String}
 */
const returnThePageId = (pages, pageNumber) => {
  let page = pages.find(page => {
    return page.number === pageNumber
  })
  return page !== undefined ? page.id : 'reset'
}

/**
 * Vérifie si toutes les questions obligatoires ont une réponse.
 * Le 1er filtre ne garde que les questions.
 * La seconde méthode, renvoie si les réponses sont bien fournies
 *
 * @param pageItems
 * @return {boolean} Retourne vrai s'il y a des erreurs et faux s'il n'y en a pas.
 */
const returnIfPageErrors = (pageItems) => {
  return !pageItems
    .filter(item => item.type === 'question')
    .every(item => {
      // Si le scale n'est pas défini ce n'est pas une question de Likert
      return item.scale === undefined
        ? !item.definition.required || (item.answer.answer !== null && item.answer.answer !== '')
        : item.answer.answer !== null
    })
}

/**
 * @typedef {Object} UserQuestionnaireState
 * @property archivePage {PageAir} Page courante sur laquelle on travaille archivée pour vérification avant PATCH.
 * @property changePage {Boolean} Variable récupérée par Vuelidate pour lancer les vérifications dans tous les composants quand on essaye de changer de page.
 * @property currentPage {PageAir} Page courante sur laquelle on travaille.
 * @property finishedIterations {ResultsIterations} Itérations terminées pour le questionnaire courant
 * @property lastLikertQuestion {LikertQuestionAir} Stocke la dernière question de Likert qu'on a affiché pour savoir si on fusionne les échelles.
 * @property nextPage {PageAir} Page suivante pré-chargée.
 * @property positionPage {Number} Curseur qui indique la page en cours pour un questionnaire donné.
 * @property previousPage {PageAir} Page précédente pré-chargée.
 * @property progressValue {Number} Valeur d'échèvement du questionnaire.
 * @property questionnaire {UserQuestionnaireAir} Questionnaire affiché quand l'utilisateur passe le questionnaire.
 * @property questionnaires {UserQuestionnaireAir[]} Liste des questionnaires chargés lorsque l'utilisateur affiche la liste des questionnaires.
 * @property resources {Number}
 * @property resultNumber {Number}
 * @property results {Results[]}
 * @property specialFeedback {String|null}
 * @property threshold {Threshold}
 *
 * @type {UserQuestionnaireState}
 */
const state = {
  archivePage: {},
  changePage: false,
  currentPage: {},
  /**
   * @typedef {Object} ResultsIterations
   * @property {Number} id - Identifiant de l'itération
   * @property {Date} finishedAt - Date de clôture de l'itération
   */
  finishedIterations: [],
  lastLikertQuestion: {},
  nextPage: {},
  positionPage: 1,
  previousPage: {},
  progressValue: 0,
  /**
   * @typedef {Object} UserPageAir
   * @property {Number} id - Identifiant de la page
   * @property {number} number - Numéro de page
   */
  /**
   * @typedef {Object} IterationAir
   * @property {Number} id - Identifiant de l'itération
   * @property {number} number - Nombre de fois que l'utilisateur a passé le questionnaire
   * @property {Boolean} isFinished - Indique si l'itération est actuellement clôturée
   * @property {Date} createdAt - Date à laquelle l'itération a été crée
   * @property {Date} updatedAt - Date à laquelle l'itération a été modifiée
   * @property {Date|null} finishedAt - Date à laquelle l'itération a été clôturée
   * @property {Number|null} positionPage - Identifiant de la page où l'utilisateur s'est arrêté
   * @property {Number|null} positionQuestion - Identifiant de la question où l'utilisateur s'est arrêté
   /**
   * @typedef {Object} UserQuestionnaireAir
   * @property {Number} id - Identifiant du questionnaire
   * @property {String} label - Label du questionnaire
   * @property {String} description - Description du questionnaire
   * @property {Date} createdAt - Date de création du questionnaire
   * @property {Date} updatedAt - Date de mise à jour du questionnaire
   * @property {Date} publicationDate - Date de publication du questionnaire
   * @property {Boolean} isPublished - Indique si le questionnaire est publié
   * @property {Number} user - Identifiant de l'utilisateur propriétaire du questionnaire
   *                         -> Utilisé pour ne pas envoyer les réponses si on teste le questionaire d'un autre
   * @property {UserPageAir[]} pages - Liste des pages associées au questionnaire
   * @property {IterationAir[]} iterations - Liste des itérations liées au questionnaire
   */
  questionnaire: {},
  questionnaires: [],
  resultNumber: 0,
  /**
   * @typedef {Object} SkillResult
   * @property {String} skillId - Identifiant de la compétence
   * @property {String} name - Nom complet de la compétence (affiché au survol)
   * @property {String} shortName - Nom court pour la compétence (affiché autour du radar)
   * @property {Number[]} results - Résultats des question de Likert rattachés à la compétence
   * @property {Number} order - Ordre d'affichage des feedback au sein du domaine
   * @property {String} feedback - Texte du feedback
   */
  /**
   * @typedef {Object} Results
   * @property {String} domainId - ID du domaine
   * @property {Boolean} chartDisplay
   * @property {String} name
   * @property {SkillResult[]} skills
   */
  results: [],
  specialFeedback: null,
  /**
   * @typedef {Object} Threshold
   * @property {Number} high - Valeur du seuil haut
   * @property {Number} low - Valeur du seuil bas
   */
  threshold: {}
}

const getters = {
  changePage: state => state.changePage,
  currentPage: state => state.currentPage,
  finishedIterations: state => state.finishedIterations,
  lastLikertQuestion: state => state.lastLikertQuestion,
  nextPage: state => state.nextPage,
  positionPage: state => state.positionPage,
  previousPage: state => state.previousPage,
  progressValue: state => state.progressValue,
  questionnaire: state => state.questionnaire,
  userQuestionnaires: state => state.questionnaires,
  resultNumber: state => state.resultNumber,
  results: state => state.results,
  specialFeedback: state => state.specialFeedback,
  threshold: state => state.threshold
}

const actions = {
  /**
   * Crée une itération s'il n'y en a pas ou s'il n'y en a que des fermées.
   *
   * @param context
   * @param id {Number} Identifiant que questionnaire à passer pour créer l'itération
   * @return {Promise<void>}
   */
  createIteration: ({ commit, dispatch }, id) => {
    return _axios.post(`user/iterations/`, { questionnaire: id })
      .then(response => {
        commit('ADD_ITERATION', response.data)
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  /**
   * Utilisé pour supprimer une itération quand l'utilisateur souhaite recommencer un questionnaire
   * depuis le début.
   *
   * @param {Object} context
   * @param {Object} payload
   * @param {UserQuestionnaireAir} payload.questionnaire
   * @param {Boolean} payload.isTest
   * @return {Promise<void>}
   */
  deleteIteration: ({ commit, dispatch }, { questionnaire, isTest }) => {
    const id = returnIterationNotFinished(isTest ? null : questionnaire).id

    return _axios.delete(`user/iterations/${id}/`)
      .then(() => {
        commit('RESET_QUESTIONNAIRE')
        isTest
          ? router.push({ name: 'home_user' })
          : router.push({
            name: 'userQuestionnairePass',
            params: { id: questionnaire.id }
          })
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  deleteFinishedIterations: ({ commit, state }) => {
    const deletePromises = []
    state.finishedIterations.forEach(
      iteration => {
        deletePromises.push(
          _axios.delete(`user/iterations/${iteration.id}/`)
        )
      }
    )
    return Promise.all(deletePromises)
      .then(() => {
        commit('RESET_ITERATIONS')
        router.push({
          name: 'home_user'
        })
      })
  },
  /**
   * Pré-charge la page précédente et la suivante.
   *
   * @param context
   * @param ids {Object}
   * @param ids.previous {Number|String} Identifiant de la page précédente ou la commande "reset"
   * @param ids.next {Number|String} Identifiant de la page suivante ou la commande "reset"
   */
  loadPreviousAndNextPage: ({ commit }, { previous, next }) => {
    if (previous === 'reset') {
      commit('RESET_PAGE', false)
    } else if (previous !== null) {
      _axios.get(`user/pages/${previous}/`)
        .then(response => {
          commit('LOAD_PAGE', {
            page: response.data,
            previous: true,
            next: false
          })
        })
    }

    if (next === 'reset') {
      commit('RESET_PAGE', true)
    } else if (next !== null) {
      _axios.get(`user/pages/${next}/`)
        .then(response => {
          commit('LOAD_PAGE', {
            page: response.data,
            previous: false,
            next: true
          })
        })
    }
  },
  /**
   * Charge une page et lance le pré-chargement de la page suivante et la précédente.
   *
   * @param context
   * @param ids {Object}
   * @param ids.id {Number} Identifiant de la page à charger
   * @param ids.previous {Number|null} Identifiant de la page précédente à charger ou null s'il n'y a pas.
   * @param ids.next {Number|null} Identifiant de la page suivante à charger ou null s'il n'y a pas.
   * @return {Promise<void>}
   */
  loadPage: ({ commit, dispatch }, ids) => {
    return _axios.get(`user/pages/${ids.id}/`)
      .then(response => {
        commit('CALCULATE_PROGRESSION')
        commit('LOAD_PAGE', {
          page: response.data,
          previous: false,
          next: false
        })
        dispatch('loadPreviousAndNextPage', {
          previous: ids.previous === null ? 'reset' : ids.previous,
          next: ids.next === null ? 'reset' : ids.next
        })
      })
  },
  /**
   * Charge le questionnaire lorsque l'utilisateur passe le questionnaire.
   *
   * @param commit
   * @param state
   * @param dispatch
   * @param id {number} Identifiant du questionnaire à charger
   * @return {Promise<void>}
   */
  loadQuestionnaire: ({ commit, state, dispatch }, id) => {
    return _axios.get(`user/questionnaires/${id}/`)
      .then(async response => {
        commit('LOAD', {
          data: response.data,
          oneQuestionnaire: true
        })

        // Crée une itération s'il y en a pas de non clôturé
        const unfinishedIteration = returnIterationNotFinished()
        if (unfinishedIteration === undefined) {
          await dispatch('createIteration', state.questionnaire.id)
        }

        commit('INIT_POSITION_QUESTIONNAIRE', unfinishedIteration)
      })
  },
  /**
   * Charge la liste des questionnaires.
   *
   * @param commit
   * @return {Promise<void>}
   */
  loadUserQuestionnaires: ({ commit }) => {
    return _axios.get('user/questionnaires/')
      .then(response => {
        commit('LOAD', {
          data: response.data,
          oneQuestionnaire: false
        })
      })
  },
  loadResults: ({ commit, dispatch }, questionnaireId) => {
    return _axios.get(`user/questionnaires/${questionnaireId}/get_results/`)
      .then(response => {
        if (response.status === 204) {
          dispatch(
            'displayError',
            {
              action: i18n.t('store.user.noResult'),
              error: { response }
            },
            { root: true }
          )
        } else {
          const { domains, specialFeedback, iterations } = response.data
          commit('LOAD_RESULTS', domains)
          commit('LOAD_SPECIAL_FEEDBACK', specialFeedback)
          commit('LOAD_ITERATIONS', iterations)
        }
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
        router.push({ name: 'home_user' })
      })
  },
  /**
   * Charge les seuils pour les afficher dans le graphique des résultats.
   *
   * @param context
   * @return {Promise<void>}
   */
  loadThreshold: ({ commit, state }) => {
    if (!state.threshold.high) {
      return _axios.get('threshold/')
        .then(response => {
          commit('LOAD_THRESHOLD', response.data)
        })
    }
  },
  /**
   * Passe à la page précédente :
   * Vérifie si on n'est pas déjà à la première page.
   * Décrémente la page sur laquelle on se trouve.
   * Récupère les pages nécessaires dans celles déjà chargées.
   * Pré-charge la page précédente.
   *
   * @param context
   * @param context.commit
   * @param context.state {state}
   * @param context.dispatch
   */
  navPreviousPage: ({ commit, state, dispatch }) => {
    --state.positionPage
    commit('NAV_PREVIOUS_PAGE')
    commit('CALCULATE_PROGRESSION')

    _axios.patch(
      `user/iterations/${returnIterationNotFinished().id}/`,
      { position_page: state.previousPage.id }
    )
      .then(response => {
        returnIterationNotFinished().positionPage = response.data.position_page
      })

    dispatch('loadPreviousAndNextPage', {
      previous: returnThePageId(state.questionnaire.pages, state.positionPage - 1),
      next: null
    })
    commit('SWITCH_CHANGE_PAGE', false)
  },
  /**
   * Passe à la page suivante :
   * Vérifie si on n'est pas déjà à la dernière page.
   * Incrémente la page sur laquelle on se trouve.
   * Récupère les pages nécessaires dans celles déjà chargées.
   * Pré-charge la page suivante.
   *
   * @param context
   * @param context.commit
   * @param context.state {state}
   * @param context.dispatch
   */
  navNextPage: ({ commit, state, dispatch }) => {
    commit('SWITCH_CHANGE_PAGE', true)
    if (state.positionPage !== state.questionnaire.pages.length &&
      !returnIfPageErrors(state.currentPage.pageItems)) {
      ++state.positionPage
      commit('NAV_NEXT_PAGE')
      commit('CALCULATE_PROGRESSION')

      _axios.patch(
        `user/iterations/${returnIterationNotFinished().id}/`,
        { 'position_page': state.nextPage.id }
      )
        .then(response => {
          returnIterationNotFinished().positionPage = response.data.position_page
        })

      dispatch('loadPreviousAndNextPage', {
        previous: null,
        next: returnThePageId(state.questionnaire.pages, state.positionPage + 1)
      })
      commit('SWITCH_CHANGE_PAGE', false)
    }
  },
  /**
   * Sauvegarde dans le store la question de Likert qu'on vient de stoker pour permettre de vérifier si on en charge tout de suite une après si elle a la même échelle.
   * Permet de fusionner les échelles pour les questions de Likert qui ont les mêmes échelles.
   *
   * @param context
   * @param question {LikertQuestionAir}
   */
  saveLikertQuestion: ({ commit }, question) => {
    commit('SAVE_LIKERT', question)
  },
  /**
   * Lorsque l'utilisateur renseigne une réponse la méthode crée la request et l'envoie au serveur.
   * Vérifie soit que c'est un POST ou que si c'est une modif l'élément est différent de celui archivé.
   * Récupère si c'est une question de Likert pour traiter les valeurs avant de les envoyer.
   *
   * @param context
   * @param payload
   * @param payload.questionId {Number} Identifiant de la question associé à la réponse
   * @param payload.answer {Object} Réponse à sauvegarder
   */
  sendAnswer: ({ commit, state }, { questionId, answer, likertDefinition }) => {
    const noAnswerId = answer.id === undefined
    let answerSend = answer.answer

    let archiveAnswer = noAnswerId
      ? null
      : state.archivePage.pageItems.find(
        item => (item.type === 'question') && (item.answer.id === answer.id)
      )

    if (likertDefinition && likertDefinition.scaleInverted && answerSend !== -1) {
      answerSend = 5 - answerSend
    }
    const data = {
      answer: answerSend,
      ...noAnswerId ? {
        question: questionId,
        questionnaire: state.questionnaire.id
      } : {}
    }

    if (noAnswerId) {
      return _axios.post('user/answers/', data)
        .then(response => {
          commit('ADD_ANSWER', {
            answer: response.data,
            likertDefinition
          })
        })
    } else if (archiveAnswer && answerSend !== archiveAnswer.answer) {
      return _axios.patch(`user/answers/${answer.id}/`, data)
        .then(response => {
          commit('ADD_ANSWER', {
            answer: response.data,
            likertDefinition
          })
        })
    }
  },
  /**
   * Lance la méthode pour terminer l'itération.
   * Lance la vérification si la page est correctement terminée.
   *
   * @param context
   */
  terminate: ({ commit }) => {
    commit('SWITCH_CHANGE_PAGE', true)
    if (!returnIfPageErrors(state.currentPage.pageItems)) {
      const id = returnIterationNotFinished().id
      const questionnaireId = state.questionnaire.id

      _axios.patch(`user/iterations/${id}/finish/`)
        .then(() => {
          commit('RESET_QUESTIONNAIRE')
          commit('SWITCH_CHANGE_PAGE', false)
          router.push({
            name: 'user_results',
            params: { id: questionnaireId }
          })
        })
    }
  }
}

const mutations = {
  /**
   * Charge l'index de la réponse dans le tableau de la page courante,
   * pour modifier en fonction le même item dans le tableau de la page d'archive.
   *
   * @param state
   * @param answer
   * @param likertDefinition
   * @constructor
   */
  ADD_ANSWER: (state, { answer, likertDefinition }) => {
    let answerIndex = state.currentPage.pageItems.findIndex(item => item.value === answer.question)
    if (likertDefinition) {
      const likertAnswer = parseInt(answer.answer)
      answer.answer = (likertDefinition.scaleInverted && likertAnswer >= 0)
        ? 5 - likertAnswer : likertAnswer
    }
    state.currentPage.pageItems[answerIndex].answer = { ...answer }
    state.archivePage.pageItems[answerIndex].answer = { ...answer }
  },
  /**
   * Ajoute une itération au questionnaire en cours après une la création d'une nouvelle itération.
   *
   * @param state
   * @param newIteration {IterationDJ}
   */
  ADD_ITERATION: (state, newIteration) => {
    state.questionnaire.iterations.push(
      UserQuestionnaireConverter.DJToJS.convertIteration(newIteration)
    )
  },
  CALCULATE_PROGRESSION: (state) => {
    state.progressValue = Math.round(
      (state.positionPage - 1) / state.questionnaire.pages.length * 100
    )
  },
  /**
   * Initie la position du questionnaire lors de l'affichage,
   * à partir des informations contenues dans l'itération.
   *
   * @param state
   * @param iteration {IterationAir}
   */
  INIT_POSITION_QUESTIONNAIRE: (state, iteration) => {
    state.positionPage = iteration
      ? (state.questionnaire.pages.findIndex(page => page.id === iteration.positionPage) + 1)
      : 1
  },
  /**
   * Sauvegarde un questionnaire ou une liste de questionnaires dans le store.
   *
   * @param state
   * @param data {UserQuestionnaireDJ|UserQuestionnaireDJ[]}
   * @param oneQuestionnaire {Boolean} Indique si on est sur un ou une liste de questionnaires
   */
  LOAD: (state, { data, oneQuestionnaire }) => {
    state.questionnaires = []
    oneQuestionnaire
      ? state.questionnaire = UserQuestionnaireConverter.DJToJS.convertQuestionnaire(data)
      : state.questionnaires = data.map(
        q => UserQuestionnaireConverter.DJToJS.convertQuestionnaire(q)
      )
  },
  LOAD_ITERATIONS: (state, iterations) => {
    state.finishedIterations = iterations.map(iteration => {
      iteration.finishedAt = new Date(iteration.finishedAt * 1000)
        .toLocaleDateString(navigator.language)
      return iteration
    })
  },
  LOAD_PAGE: (state, { page, previous, next }) => {
    const pageConverted = QuestionnaireConverter.DJToJS.convertPage(page, true)
    if (previous) {
      state.previousPage = pageConverted
    } else if (next) {
      state.nextPage = pageConverted
    } else {
      state.currentPage = pageConverted
      state.archivePage = JSON.parse(JSON.stringify(pageConverted))
    }
  },
  LOAD_RESULTS: (state, results) => {
    state.results = results
    state.resultNumber = results.length > 0 ? results[0].skills[0].results.length : 0
  },
  LOAD_SPECIAL_FEEDBACK: (state, feedback) => {
    state.specialFeedback = feedback
  },
  LOAD_THRESHOLD: (state, threshold) => {
    state.threshold = threshold
  },
  NAV_PREVIOUS_PAGE: (state) => {
    state.nextPage = Object.assign({}, state.currentPage)
    state.currentPage = Object.assign({}, state.previousPage)
    state.archivePage = JSON.parse(JSON.stringify(state.previousPage))
  },
  NAV_NEXT_PAGE: (state) => {
    state.previousPage = Object.assign({}, state.currentPage)
    state.currentPage = Object.assign({}, state.nextPage)
    state.archivePage = JSON.parse(JSON.stringify(state.nextPage))
  },
  RESET_ITERATIONS: state => {
    state.finishedIterations = []
  },
  /**
   * Réinitialise le stockage de question précédente ou suivante pour permettre de vérifier,
   * pour l'affichage des boutons, si on a encore un questionnaire dans le store.
   *
   * @param state
   * @param next {Boolean} indique si c'est le next qu'on vide ou le previous
   * @constructor
   */
  RESET_PAGE: (state, next) => {
    next
      ? state.nextPage = {}
      : state.previousPage = {}
  },
  /**
   * Une fois un questionnaire clôturé, remet les variables du state à z&ro.
   *
   * @param state
   * @constructor
   */
  RESET_QUESTIONNAIRE: (state) => {
    state.currentPage = Object.assign({})
    state.lastLikertQuestion = Object.assign({})
    state.questionnaire = Object.assign({})
    state.positionPage = 1
  },
  SAVE_LIKERT: (state, question) => {
    state.lastLikertQuestion = JSON.parse(JSON.stringify(question))
  },
  SWITCH_CHANGE_PAGE: (state, value) => {
    state.changePage = value
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
